import { PropertyTool, type UpdatePropertyInput } from '@/backend/types'
import type {
  BaseUpdatePropertyParams,
  UpdateCollectionPropertyConfig,
  UpdatePropertyParams,
  UpdateSelectPropertyConfig,
} from '@/backend/updateProperty'
import { deserializePropertyInput } from '@/modules/Project/Properties/deserializePropertyInput.ts'
import type { Property } from '@/modules/Project/Properties/types'
import { isSelectProperty } from '@/shared/utils/typeGuards'

/**
 * Given a property (using the FE domain object) and a set of deltas for
 * updating/removing select/collection options, this function will return
 * an object that can be passed to our backend `updateProperty` function.
 */
export function getPropertyUpdateConfig(
  property: Property,
  params: {
    upsertOptions: UpdateSelectPropertyConfig['upsertOptions']
    removeOptions: UpdateSelectPropertyConfig['removeOptions']
    upsertCollectionProperties: UpdateCollectionPropertyConfig['upsertCollectionProperties']
    removeCollectionProperties: UpdateCollectionPropertyConfig['removeCollectionProperties']
  },
): UpdatePropertyParams {
  const baseParams: BaseUpdatePropertyParams = {
    description: property.description?.trim() || null,
    isGrounded: property.isGrounded,
    name: property.name,
    tool: property.tool,
    reviewable: property.reviewable,
    visibleInSidebar: property.visibleInSidebar,
    inputs:
      property.tool === PropertyTool.manual
        ? []
        : property.inputs.map<UpdatePropertyInput>(deserializePropertyInput),
    thinkingEffort: property.toolConfig.thinking_effort,
  }

  if (isSelectProperty(property)) {
    const config: UpdateSelectPropertyConfig = {
      defaultOption: property.config.defaultOption,
      removeOptions: params.removeOptions,
      upsertOptions: params.upsertOptions?.filter((option) => {
        return option.value.trim() !== '' && option.new_value?.trim() !== ''
      }),
    }

    if (property.type === 'multi_select') {
      config.maxSelected = property.config.maxSelected
    }

    return {
      ...baseParams,
      type: property.type,
      config,
    }
  }

  if (property.type === 'collection') {
    return {
      ...baseParams,
      type: property.type,
      config: {
        upsertCollectionProperties: params.upsertCollectionProperties,
        removeCollectionProperties: params.removeCollectionProperties,
      },
    }
  }

  if (property.type === 'file_collection') {
    return {
      ...baseParams,
      type: property.type,
      config: {
        upsertCollectionProperties: params.upsertCollectionProperties,
        removeCollectionProperties: params.removeCollectionProperties,
        pdfSplitSize: property.config.pdfSplitSize,
        pdfSplitMode: property.config.pdfSplitSize === 1 ? 'single_image' : 'fixed_size',
      },
    }
  }

  if (property.type === 'reference') {
    return {
      ...baseParams,
      type: property.type,
      config: {
        entityLimit: property.config.entityLimit,
      },
    }
  }

  if (property.type === 'number') {
    return {
      ...baseParams,
      type: property.type,
      config: {
        format: property.config.isCustomFormat ? 'custom' : 'auto',
        format_custom: {
          decimal_places: property.config.format.decimalPlaces,
          negative_format: property.config.format.negativeFormat,
          right_align: property.config.format.rightAlign,
          thousand_separator: property.config.format.thousandsSeparator ? 'comma' : 'none',
        },
      },
    }
  }

  return {
    ...baseParams,
    type: property.type,
  }
}
